import { useMemo } from 'preact/hooks';

import { FacetDialogName } from 'Components/facetPanel/common/index';
import DialogButton, { type DialogButtonTemplateParams } from 'Components/smallComponents/dialogButton';
import type { TemplateFunction, TemplateFunctionInvoker, TemplateResult } from 'Components/types';
import {
  createFilteredResponseSelectionSelector,
  createShouldShowFacetPanelSelector,
  selectedFacetsCountSelector,
} from 'Core/selectors/search';
import { useSelector } from 'Core/store';
import widgetsConfig from 'Models/uiConfig/componentlessWidgetsConfig';
import type { AnyWidget, Widget } from 'Templates/widgets';
import { cloneSafe } from 'Utils/components';

function isFacetDialogWidget(w: AnyWidget): w is Widget<'FacetDialog'> {
  return w.type === 'FacetDialog';
}

// TODO: load widgets from props
const facetDialogWidget = widgetsConfig.find(isFacetDialogWidget);

interface Props {
  template: TemplateFunction<FacetDialogButtonTemplateParams>;
}

export interface FacetDialogButtonTemplateParams {
  dialogButton: TemplateFunctionInvoker<DialogButtonTemplateParams>;
}

export default function FacetDialogButton({ template }: Props): TemplateResult {
  const { fields, ignoreFields } = facetDialogWidget ?? {};
  const showButton = useShowButton(fields, ignoreFields);
  const withSelection = useWithSelection(fields, ignoreFields);
  const selectedFacetsCount = useSelector(selectedFacetsCountSelector);

  const dialogButton = (templ: TemplateFunction<DialogButtonTemplateParams>) => {
    if (!showButton) {
      return null;
    }
    const props = {
      template: templ,
      dialogName: facetDialogWidget ? FacetDialogName : 'facet-dialog',
      templateParams: { withSelection, selectedFacetsCount },
    };
    return <DialogButton {...props} key="button" />;
  };

  const appendedClasses = withSelection && 'with-selection';

  const component = template.call({ dialogButton });

  return cloneSafe(component, null, { appendedClasses: appendedClasses || undefined });
}

function useShowButton(fields?: string[], ignoreFields?: string[]) {
  return useSelector(
    useMemo(() => createShouldShowFacetPanelSelector(fields ?? [], ignoreFields), [fields, ignoreFields]),
  );
}

function useWithSelection(fields?: string[], ignoreFields?: string[]) {
  const filteredSelector = useMemo(
    () => createFilteredResponseSelectionSelector(fields ?? [], ignoreFields),
    [fields, ignoreFields],
  );
  return useSelector((state) => !!filteredSelector(state).length);
}
